import { isValidActivityName } from '../headers'
import { ActionDefinition } from '../types'

import { appointmentActions } from './appointment'
import { appointmentNotificationActions } from './appointment-notifications'
import { attendanceActions } from './attendance'
import { cancellationActions } from './cancellation'
import { caseDetailsActions } from './case-details'
import { caseStatusActions } from './case-status'
import { commonActions } from './common'
import { correspondenceActions } from './correspondence'
import { dictationReportActions } from './dictation-report'
import { documentActions } from './documents'
import { imePageActions } from './ime-page'
import { paperworkActions } from './paperwork'
import { paymentActions } from './payment'
import { quotationActions } from './quotation'

export { defineActions } from './defineActions'

/**
 * The action catalog: the only place that names the tracked actions. One file per area; add an
 * action to its area's file and use its name on the button (trackAction="...").
 */
export const ACTIVITY_ACTION_AREAS = {
  'ime-page': imePageActions,
  'case-status': caseStatusActions,
  'case-details': caseDetailsActions,
  quotation: quotationActions,
  appointment: appointmentActions,
  'appointment-notifications': appointmentNotificationActions,
  paperwork: paperworkActions,
  attendance: attendanceActions,
  'dictation-report': dictationReportActions,
  payment: paymentActions,
  documents: documentActions,
  correspondence: correspondenceActions,
  cancellation: cancellationActions,
  common: commonActions,
} as const

export const ACTIVITY_ACTIONS = {
  ...imePageActions,
  ...caseStatusActions,
  ...caseDetailsActions,
  ...quotationActions,
  ...appointmentActions,
  ...appointmentNotificationActions,
  ...paperworkActions,
  ...attendanceActions,
  ...dictationReportActions,
  ...paymentActions,
  ...documentActions,
  ...correspondenceActions,
  ...cancellationActions,
  ...commonActions,
}

/** The name of an action of the catalog. */
export type ActivityActionName = Extract<keyof typeof ACTIVITY_ACTIONS, string>

const catalog: Record<string, ActionDefinition> = ACTIVITY_ACTIONS

/** The catalog entry of `name`, or undefined when the catalog has no such action. */
export const getActionDefinition = (
  name: string
): ActionDefinition | undefined =>
  typeof name === 'string' &&
  Object.prototype.hasOwnProperty.call(catalog, name)
    ? catalog[name]
    : undefined

/** Whether requests made for `name` are tagged: it is in the catalog and not disabled. */
export const isActionEnabled = (name: string): boolean => {
  const definition = getActionDefinition(name)
  return !!definition && definition.enabled !== false
}

/** What the history shows for an action name: its catalog label, else the name itself. */
export const getActionLabel = (name: string): string =>
  getActionDefinition(name)?.label ?? name

/**
 * What is wrong with a set of catalog areas: names the backend would reject, names declared in
 * more than one area, entries without a label or area. Empty when the catalog is sound (a unit
 * test keeps it that way).
 */
export const findCatalogProblems = (
  areas: Record<
    string,
    Record<string, ActionDefinition>
  > = ACTIVITY_ACTION_AREAS
): string[] => {
  const problems: string[] = []
  const declaredIn = new Map<string, string>()
  Object.entries(areas).forEach(([area, actions]) => {
    Object.entries(actions).forEach(([name, definition]) => {
      if (!isValidActivityName(name)) {
        problems.push(`${area}: "${name}" is not a valid action name`)
      }
      if (declaredIn.has(name)) {
        problems.push(
          `${area}: "${name}" is already declared in ${declaredIn.get(name)}`
        )
      } else {
        declaredIn.set(name, area)
      }
      if (!definition?.label?.trim() || !definition?.area?.trim()) {
        problems.push(`${area}: "${name}" needs a label and an area`)
      }
    })
  })
  return problems
}
